import { useRef, useState } from "react"
import {
  AlertDialog,
  AlertDialogLabel,
  AlertDialogDescription,
} from "@reach/alert-dialog"
import { Icon } from "source/shared/components"
import { Elements } from "@stripe/react-stripe-js"
import { PaymentElementLoading } from "source/payments/components/AddPaymentMethodDialog/PaymentElementLoading"
import { AddPaymentMethodForm } from "source/payments/components/AddPaymentMethodDialog/AddPaymentMethodForm"
import { useStripeConfig } from "../hooks/useStripeConfig"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { StripePaymentMethodDataWithMetadata } from "source/giving/donate/form/types"
import { StripeElements } from "@stripe/stripe-js"

interface AddPaymentMethodModalProps {
  onCancel: () => void
  onConfirm: () => void
  onSuccess: (newPaymentMethodId: string) => void
  stripe: {
    publishable_key: string
    account_identifier: string
    test_mode: boolean
  }
  organizationId: string
}

export default function AddPaymentMethodModal({
  onCancel,
  onConfirm,
  onSuccess,
  stripe: stripeProps,
  organizationId,
}: AddPaymentMethodModalProps) {
  const cancelRef = useRef(null)

  const [addPaymentMethodError, setAddPaymentMethodError] = useState<
    string | null
  >(null)
  const [paymentMethodSaving, setPaymentMethodSaving] = useState<
    boolean | null
  >(null)

  const {
    stripe,
    stripeOptions,
    paymentElementOptions,
    loading: stripeLoading,
  } = useStripeConfig(stripeProps)

  const submitPaymentMethodToStripe = async (
    stripeElements: StripeElements,
  ) => {
    if (!stripe || !stripeElements) return
    setAddPaymentMethodError(null)
    setPaymentMethodSaving(true)

    try {
      const { setupIntent } = await stripe.confirmSetup({
        elements: stripeElements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            allow_redisplay: "always",
            metadata: {
              planning_center_source_app: "Church Center",
              planning_center_organization_id: organizationId,
            },
          } as StripePaymentMethodDataWithMetadata,
        },
      })
      setAddPaymentMethodError(null)
      try {
        const newPaymentMethodId = await updateCustomerPaymentMethod(
          setupIntent?.id as string,
        )
        onSuccess(newPaymentMethodId)
        onConfirm()
      } catch {
        setAddPaymentMethodError(
          "We encountered a problem while attempting to save your payment method.",
        )
      }
    } catch {
      setAddPaymentMethodError(
        "Stripe encountered a problem while attempting to save your payment method.",
      )
    } finally {
      setPaymentMethodSaving(false)
    }
  }

  const updateCustomerPaymentMethod = async (
    stripe_setup_intent_identifier: string,
  ): Promise<string> => {
    const response = await sessionApiClient.post(
      "/giving/v2/me/payment_methods",
      {
        data: {
          type: "PaymentMethod",
          attributes: {
            stripe_setup_intent: stripe_setup_intent_identifier,
          },
        },
      },
    )
    return response.data.id
  }

  return (
    <AlertDialog leastDestructiveRef={cancelRef}>
      <AlertDialogLabel>
        <div className="d-f jc-sb ai-c">
          <span>Add payment method</span>{" "}
          <button
            ref={cancelRef}
            className="btn sm-btn naked-btn"
            onClick={onCancel}
            disabled={!!paymentMethodSaving}
          >
            <Icon className="fs-3 c-tint3" symbol="general#x" />
          </button>
        </div>
      </AlertDialogLabel>
      <AlertDialogDescription>
        {stripeLoading ? (
          <PaymentElementLoading />
        ) : (
          stripe && (
            <Elements stripe={stripe} options={stripeOptions}>
              <AddPaymentMethodForm
                paymentElementOptions={paymentElementOptions}
                onSubmit={submitPaymentMethodToStripe}
                saving={paymentMethodSaving}
                error={addPaymentMethodError}
              />
            </Elements>
          )
        )}
      </AlertDialogDescription>
    </AlertDialog>
  )
}
